"use client";
import { useEffect, useState, memo, useRef } from "react";
import { EditorialListingsProps } from "./types";
import propsToAttributes from "@/utils/propsToAttributes";
import { StackedELSkeletonLoader } from "./loaders";

const DEFAULT_STYLE = `
  --component-border-size:0;
  --component-border-radius: 12px;
  --component-background: #113742;
  --title-text-color: white;
  --subtitle-text-color: white;
  --sonic-tooltip-border-color: white;
  --sonic-tooltip-label-color:white;
  --cta-card-button-border-radius: 10px;
  --header-text-align: left;
`;

const DEFAULT_PROPS: EditorialListingsProps = {
  layout: "stacked",
  showRedirectNotice: "true",
  limit: "4",
  buttonText: "Visit Site",
  advertisementText: "Advertisement",
  degree: "general",
  category: "general",
  subject: "general",
  title: "These Schools Are Accepting Students Now",
  subtitle: "Find the program that works best for you and apply today.",
  moreButtonText: "View More Programs",
  showMoreResults: "false",
  hideViewMoreButton: "true",
  swapElTitle: "false",
  ctaExternalRedirectIcon: "true",
  style: DEFAULT_STYLE,
  adDisclaimerText:
    "This is a paid advertisement. edX and its parent company, 2U, are compensated for paid promotional advertisements on this site. This compensation does not influence our editorial content, such as resource guides and articles.",
};

const AffiliateEL = memo((props: EditorialListingsProps) => {
  const affiliateAttributes = propsToAttributes(props);
  return (
    <div
      dangerouslySetInnerHTML={{
        __html: `<affiliate-el${affiliateAttributes}>`,
      }}
    />
  );
});
AffiliateEL.displayName = "AffiliateEL";

const AffiliateELWrapper = (props: EditorialListingsProps) => {
  const mergedProps: EditorialListingsProps = { ...DEFAULT_PROPS, ...props };
  const [isLoaded, setIsLoaded] = useState(false);
  const affiliateRef = useRef<HTMLDivElement>(null);
  const attemptCountRef = useRef(0);
  const MAX_ATTEMPTS = 10;

  useEffect(() => {
    const checkComponent = () => {
      attemptCountRef.current += 1;

      // Get the affiliate element from the ref's DOM node
      const affiliateElement =
        affiliateRef.current?.querySelector("affiliate-el");

      if (affiliateElement?.shadowRoot) {
        setIsLoaded(true);
        return true;
      }

      if (attemptCountRef.current >= MAX_ATTEMPTS) {
        return true;
      }

      return false;
    };

    const id = setInterval(() => {
      if (checkComponent()) {
        clearInterval(id);
      }
    }, 1000);

    return () => clearInterval(id);
  }, []);

  return (
    <div className="mt-10" ref={affiliateRef}>
      {!isLoaded && (
        <StackedELSkeletonLoader limit={parseInt(mergedProps.limit ?? "3")} />
      )}
      <div className={`${!isLoaded ? "hidden" : ""}`}>
        <AffiliateEL {...mergedProps} />
      </div>
    </div>
  );
};

export default AffiliateELWrapper;
